import { ApplicationContext } from './context/application.context';
import { Injectable, Optional } from './dependency-injection/decorators';
import { ApplicationLifecycleEvent } from './event-manager/event/application-lifecycle.event';
import { Events } from './event-manager/event/events';
import { EventManager } from './event-manager/event-manager';
import { IntegrationStoppedExecutionException } from './exception/integration-stopped-execution.exception';
import { IntegrationRunner } from './integrations/integration-runner';
import { Logger } from './logger/logger';
import { NpiDetector } from './npi/npi.detector';
import { AdSetRenderer } from './renderer/ad-set.renderer';
import { AdSetResolver } from './resolver/ad-set.resolver';
import { MetadataResolver } from './resolver/metadata.resolver';

@Injectable()
export class Application {
  private initialized: boolean = false;

  constructor (
    private readonly context: ApplicationContext,
    private readonly adSetResolver: AdSetResolver,
    private readonly adSetRenderer: AdSetRenderer,
    private readonly npiDetector: NpiDetector,
    private readonly metadataResolver: MetadataResolver,
    private readonly eventManager: EventManager,
    private readonly integrationRunner: IntegrationRunner,
    @Optional() private readonly logger?: Logger,
  ) {
  }

  public async run (): Promise<void> {
    await this.eventManager.dispatch(Events.APPLICATION_PRE_RUN, new ApplicationLifecycleEvent(this));

    await this.init();

    const adSet = this.context.getAdSet();

    if (!adSet) {
      this.logger?.error(`Ad set with identifier ${this.context.getApplicationId()} cannot be resolved`);
      return;
    }

    this.context.setMetadata(await this.metadataResolver.resolve());

    try {
      await this.integrationRunner.run(adSet);
    } catch (e) {
      if (e instanceof IntegrationStoppedExecutionException) {
        // If one of the integrations stopped the propagation, we should not render the application
        this.context.setCanRender(false);
      } else {
        this.logger?.error(`Integration failed to run`, e);
      }
    }

    if (!this.context.canRender()) {
      await this.eventManager.dispatch(Events.APPLICATION_POST_RUN, new ApplicationLifecycleEvent(this));
      return;
    }

    await this.render();

    await this.eventManager.dispatch(Events.APPLICATION_POST_RUN, new ApplicationLifecycleEvent(this));
  }

  private async init (): Promise<void> {
    if (this.initialized) {
      return;
    }

    await this.eventManager.dispatch(Events.APPLICATION_PRE_INIT, new ApplicationLifecycleEvent(this));
    const adSet = await this.adSetResolver.resolve(this.context);

    if (!adSet) {
      this.logger?.error(`Ad set with identifier ${this.context.getApplicationId()} cannot be resolved`);
      return;
    }

    this.context.setAdSet(adSet);

    await this.npiDetector.detect(adSet);

    this.initialized = true;

    await this.eventManager.dispatch(Events.APPLICATION_POST_INIT, new ApplicationLifecycleEvent(this));
  }

  private async render (): Promise<void> {
    const adSet = this.context.getAdSet();

    if (!adSet) {
      throw new Error('Ad set is not defined');
    }

    await this.adSetRenderer.render(adSet);
  }
}
